import {
  ReprocessMessageEventSearch,
  RsvpFilterEnum,
  SchedulingProposalStateEnum,
  TagFilterEnum,
} from "@clockwise/schema";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import { isEmpty } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { GQLAttendeeFilter } from "../../components/proposal/event-search/filters";
import { GQLEventSearchBlock, GQLOrdinalModifier, GQLTimeRangeFilter } from "../../utils/types";
import { useAIMessageContext } from "../AIMessageContext";
import {
  mapEventSearchBlockToId,
  udpateRsvpFilterForEventSearchId,
  updateAttendeeFilterForEventSearchId,
  updateOrdinalModiferForEventSearchId,
  updateTagFilterForEventSearchId,
  updateTimeRangeFilterForEventSearchId,
  updateTitleFilterForEventSearchId,
} from "./updateEventSearch.util";
import { useReprocessMessage } from "./useReprocessMessage";

export type EventSearchBlockMap = { [key: string]: ReprocessMessageEventSearch };
export const useUpdateEventSearchFilters = () => {
  const { messages } = useAIMessageContext();
  const reprocessMessage = useReprocessMessage();
  const [activeEventSearchBlockMap, setActiveEventSearchBlockMap] = useState<EventSearchBlockMap>(
    {},
  );
  const track = useTracking();

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    const activeProposalWithEventSearch = messages.filter(
      (message) =>
        message.__typename === "ProposalResponse" &&
        message.proposal.state === SchedulingProposalStateEnum.ACTIVE &&
        !isEmpty(message.proposal.eventSearchBlocks),
    );

    if (lastMessage.__typename === "ProposalResponse" && !isEmpty(activeProposalWithEventSearch)) {
      // If active proposal has eventSearchBlocks
      if (
        activeProposalWithEventSearch[0].__typename === "ProposalResponse" &&
        activeProposalWithEventSearch[0] === lastMessage
      ) {
        const proposalEventSearchMap = mapEventSearchBlockToId(
          activeProposalWithEventSearch[0].proposal.eventSearchBlocks,
        );
        setActiveEventSearchBlockMap(proposalEventSearchMap);
      }
    } else if (
      lastMessage.__typename === "ViewEventsResponse" &&
      !isEmpty(lastMessage.eventSearchBlocks)
    ) {
      // If not active proposal and last assistant message is a ViewEvents response
      const viewEventSearchMap = mapEventSearchBlockToId(lastMessage.eventSearchBlocks);
      setActiveEventSearchBlockMap(viewEventSearchMap);
    } else {
      setActiveEventSearchBlockMap({});
    }
  }, [messages]);

  const updateTagFilters = useCallback(
    (eventSearchBlockId: string, conversationId: string, newValue: TagFilterEnum[]) => {
      if (isEmpty(activeEventSearchBlockMap)) return;

      const newEventMap = updateTagFilterForEventSearchId(
        activeEventSearchBlockMap,
        eventSearchBlockId,
        newValue,
      );

      setActiveEventSearchBlockMap(newEventMap);

      const updatedEventSearches = Object.keys(newEventMap).map((key) => newEventMap[key]);
      track(TrackingEvents.CHAT.EVENT_SEARCH.CHIP_REMOVED, {
        conversationId,
        eventSearchFilterEnum: "tagFilter",
        updatedPayload: updatedEventSearches,
      });
      reprocessMessage({ eventSearches: updatedEventSearches });
    },
    [activeEventSearchBlockMap, messages],
  );

  const updateRsvpFilters = useCallback(
    (eventSearchBlockId: string, conversationId: string, newValue: RsvpFilterEnum[]) => {
      if (isEmpty(activeEventSearchBlockMap)) return;

      const newEventMap = udpateRsvpFilterForEventSearchId(
        activeEventSearchBlockMap,
        eventSearchBlockId,
        newValue,
      );

      const updatedEventSearches = Object.keys(newEventMap).map((key) => newEventMap[key]);

      track(TrackingEvents.CHAT.EVENT_SEARCH.CHIP_REMOVED, {
        conversationId,
        eventSearchFilterEnum: "RsvpFilter",
        updatedPayload: updatedEventSearches,
      });
      setActiveEventSearchBlockMap(newEventMap);
      reprocessMessage({ eventSearches: updatedEventSearches });
    },
    [activeEventSearchBlockMap, messages],
  );

  const updateAttendeeFilter = useCallback(
    (
      eventSearchBlockId: string,
      conversationId: string,
      field: Extract<keyof GQLAttendeeFilter, "unresolvedAttendees" | "resolvedAttendees">,
      newValue: string[],
    ) => {
      if (isEmpty(activeEventSearchBlockMap)) return;

      const newEventMap = updateAttendeeFilterForEventSearchId(
        activeEventSearchBlockMap,
        eventSearchBlockId,
        field,
        newValue,
      );
      const updatedEventSearches = Object.keys(newEventMap).map((key) => newEventMap[key]);

      track(TrackingEvents.CHAT.EVENT_SEARCH.CHIP_REMOVED, {
        conversationId,
        eventSearchFilterEnum: "AttendeeFilter",
        field,
        updatedPayload: updatedEventSearches,
      });

      setActiveEventSearchBlockMap(newEventMap);
      reprocessMessage({
        eventSearches: updatedEventSearches,
      });
    },
    [activeEventSearchBlockMap, messages],
  );

  const updateTitleFilter = useCallback(
    (
      eventSearchBlockId: string,
      conversationId: string,
      newValue: GQLEventSearchBlock["titleFilter"] | null,
    ) => {
      if (isEmpty(activeEventSearchBlockMap)) return;

      const newEventMap = updateTitleFilterForEventSearchId(
        activeEventSearchBlockMap,
        eventSearchBlockId,
        newValue,
      );

      const updatedEventSearches = Object.keys(newEventMap).map((key) => newEventMap[key]);

      track(TrackingEvents.CHAT.EVENT_SEARCH.CHIP_REMOVED, {
        conversationId,
        eventSearchFilterEnum: "TitleFilter",
        updatedPayload: updatedEventSearches,
      });

      setActiveEventSearchBlockMap(newEventMap);
      reprocessMessage({ eventSearches: updatedEventSearches });
    },
    [activeEventSearchBlockMap, messages],
  );

  const updateTimeRangeFilter = useCallback(
    (
      eventSearchBlockId: string,
      conversationId: string,
      field: Extract<keyof NonNullable<GQLTimeRangeFilter>, "included" | "excluded">,
      newValue: string[],
    ) => {
      if (isEmpty(activeEventSearchBlockMap)) return;

      const newEventMap = updateTimeRangeFilterForEventSearchId(
        activeEventSearchBlockMap,
        eventSearchBlockId,
        field,
        newValue,
      );

      const updatedEventSearches = Object.keys(newEventMap).map((key) => newEventMap[key]);

      track(TrackingEvents.CHAT.EVENT_SEARCH.CHIP_REMOVED, {
        conversationId,
        eventSearchFilterEnum: "TimeRangeFilter",
        field,
        updatedPayload: updatedEventSearches,
      });

      setActiveEventSearchBlockMap(newEventMap);
      reprocessMessage({
        eventSearches: Object.keys(newEventMap).map((key) => newEventMap[key]),
      });
    },
    [activeEventSearchBlockMap, messages],
  );

  const updateOrdinalModifier = useCallback(
    (eventSearchBlockId: string, conversationId, newValue: GQLOrdinalModifier) => {
      if (isEmpty(activeEventSearchBlockMap)) return;
      const newEventMap = updateOrdinalModiferForEventSearchId(
        activeEventSearchBlockMap,
        eventSearchBlockId,
        newValue,
      );

      const updatedEventSearches = Object.keys(newEventMap).map((key) => newEventMap[key]);

      track(TrackingEvents.CHAT.EVENT_SEARCH.CHIP_EDITED, {
        conversationId,
        eventSearchFilterEnum: "OrdinalModifier",
        updatedPayload: updatedEventSearches,
      });

      setActiveEventSearchBlockMap(newEventMap);
      reprocessMessage({
        eventSearches: Object.keys(newEventMap).map((key) => newEventMap[key]),
      });
    },
    [activeEventSearchBlockMap, messages],
  );

  return {
    updateTagFilters,
    updateRsvpFilters,
    updateAttendeeFilter,
    updateTitleFilter,
    updateTimeRangeFilter,
    updateOrdinalModifier,
  };
};
